"use client";

import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { GripVertical, Plus, X } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslations } from "next-intl";

import { AdminDeleteConfirmDialog } from "@/components/admin/admin-delete-confirm-dialog";
import { AdminTableIconDeleteButton } from "@/components/admin/admin-table-icon-actions";
import { settingsCompactControlsClass } from "@/components/admin/settings-field";
import { Input } from "@/components/ui/input";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { cn } from "@/lib/utils";

export { SettingsFieldPanel as GridPanel } from "@/components/admin/settings-field";

export type GridCellContext = {
  row: GridRow;
  column: GridColumn;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;

  onChangeCell: (colKey: string, value: string) => void;
};

export type GridColumn = {
  key: string;
  label: string;
  locked?: boolean;
  editableLabel?: boolean;

  renderCell?: (ctx: GridCellContext) => ReactNode;

  width?: string;
};

export type GridRow = {
  id: string;
  locked?: boolean;
  cells: Record<string, string>;
};

export type GridData = {
  columns: GridColumn[];
  rows: GridRow[];
};

export type GridDataUpdater = GridData | ((prev: GridData) => GridData);

export function resolveGridData(
  next: GridDataUpdater,
  prev: GridData,
): GridData {
  return typeof next === "function" ? next(prev) : next;
}

export type GridFieldsEditorProps = {
  columns: GridColumn[];
  rows: GridRow[];
  onChange: (next: GridDataUpdater) => void;
  disabled?: boolean;

  canAddColumn?: boolean;
  canAddRow?: boolean;

  canRemoveRow?: boolean;

  minColumns?: number;
  maxColumns?: number;
  minRows?: number;
  maxRows?: number;

  maxVisibleRows?: number;

  maxBodyHeight?: CSSProperties["maxHeight"];

  pagination?: boolean;
  manualPagination?: boolean;
  paginationState?: { pageIndex: number; pageSize: number };
  onPaginationChange?: (state: { pageIndex: number; pageSize: number }) => void;
  rowCount?: number;

  onAddRow?: () => void;

  onRequestRemoveRow?: (row: GridRow, remove: () => void, anchor?: Element) => void;

  stickyEdges?: boolean;

  stickyHeader?: boolean;

  sortableColumns?: boolean;
  sortableRows?: boolean;

  hideWhenEmpty?: boolean;

  hideHeader?: boolean;

  repeatRowHeader?: boolean;
  hideActionHeader?: boolean;

  rowAlign?: "center" | "start";

  bare?: boolean;
  addColumnLabel?: string;
  addRowLabel?: string;

  hideColumnLimitHint?: boolean;

  newColumn?: Partial<Omit<GridColumn, "key">>;

  newRowCells?: (existingRows: GridRow[]) => Record<string, string>;

  columnLabelPlaceholder?: string;

  sanitizeColumnLabel?: (raw: string) => string;

  cellPlaceholder?: (colIndex: number, column: GridColumn) => string;

  emptyText?: string;

  renderRowDetails?: (row: GridRow) => ReactNode;

  rowAction?: (row: GridRow) => ReactNode;
};

const GRIP_TRACK_WIDTH = "1.25rem";

const ROW_ACTION_TRACK_WIDTH = "2rem";

const FLEX_COLUMN_MIN_WIDTH = "7rem";

const GRID_GAP_REM = 0.5;
const ROW_PADDING_X_REM = 0.75;

let idCounter = 0;
function newId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter.toString(36)}`;
}

const EditableHeader = memo(function EditableHeader({
  colKey,
  label,
  placeholder,
  disabled,
  sanitize,
  onCommit,
}: {
  colKey: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  sanitize?: (raw: string) => string;
  onCommit: (colKey: string, label: string) => void;
}) {
  const [local, setLocal] = useState(label);

  useEffect(() => {
    setLocal(label);
  }, [label, colKey]);

  return (
    <Input
      value={local}
      sanitizeValue={sanitize}
      disabled={disabled}
      placeholder={placeholder}
      className="h-7 w-full min-w-0 px-2 font-mono text-xs"
      spellCheck={false}
      onChange={(event) =>
        setLocal(sanitize ? sanitize(event.target.value) : event.target.value)
      }
      onBlur={() => onCommit(colKey, local)}
    />
  );
});

const columnDeleteBtnClass = cn(
  "absolute right-1 top-1/2 inline-flex -translate-y-1/2 items-center justify-center rounded",
  "bg-background/80 p-0.5 text-muted-foreground opacity-0 transition-opacity",
  "pointer-events-none hover:text-destructive",
  "group-hover:pointer-events-auto group-hover:opacity-100",
);

const columnGripBtnClass = cn(
  "absolute left-0.5 top-1/2 inline-flex -translate-y-1/2 items-center justify-center rounded",
  "cursor-grab touch-none bg-background/80 text-muted-foreground opacity-0 transition-opacity active:cursor-grabbing",
  "pointer-events-none hover:text-foreground",
  "group-hover:pointer-events-auto group-hover:opacity-100",
);

function ColumnHeaderContent({
  column,
  deletable,
  disabled,
  placeholder,
  sanitize,
  onCommit,
  onRemove,
}: {
  column: GridColumn;
  deletable: boolean;
  disabled?: boolean;
  placeholder?: string;
  sanitize?: (raw: string) => string;
  onCommit: (colKey: string, label: string) => void;
  onRemove: (colKey: string) => void;
}) {
  const t = useTranslations("site.ui");

  return (
    <>
      {column.editableLabel ? (
        <EditableHeader
          colKey={column.key}
          label={column.label}
          placeholder={placeholder}
          disabled={disabled}
          sanitize={sanitize}
          onCommit={onCommit}
        />
      ) : (
        <span className="w-full truncate">{column.label}</span>
      )}
      {deletable ? (
        <button
          type="button"
          aria-label={t("deleteColumn")}
          className={columnDeleteBtnClass}
          onClick={() => onRemove(column.key)}
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </>
  );
}

type ColumnHeaderProps = {
  first?: boolean;
  column: GridColumn;
  deletable: boolean;
  disabled?: boolean;
  placeholder?: string;
  sanitize?: (raw: string) => string;
  onCommit: (colKey: string, label: string) => void;
  onRemove: (colKey: string) => void;
};

function PlainColumnHeader(props: ColumnHeaderProps) {
  return (
    <div
      data-grid-first={props.first ? "" : undefined}
      className="group relative flex items-center"
    >
      <ColumnHeaderContent {...props} />
    </div>
  );
}

function SortableColumnHeader(props: ColumnHeaderProps) {
  const t = useTranslations("site.ui");
  const { column, disabled } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key, disabled: disabled || column.locked });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-grid-first={props.first ? "" : undefined}
      className="group relative flex items-center"
    >
      {!column.locked ? (
        <button
          type="button"
          ref={setActivatorNodeRef}
          disabled={disabled}
          aria-label={t("dragColumn")}
          className={columnGripBtnClass}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-3.5" />
        </button>
      ) : null}
      <ColumnHeaderContent {...props} />
    </div>
  );
}

function RowInputs({
  row,
  columns,
  disabled,
  rowDeletable,
  canRemoveRow = true,
  cellPlaceholder,
  onUpdateCell,
  onRemoveRow,
  rowAction,
}: {
  row: GridRow;
  columns: GridColumn[];
  disabled?: boolean;
  rowDeletable: boolean;
  canRemoveRow?: boolean;
  cellPlaceholder?: (colIndex: number, column: GridColumn) => string;
  onUpdateCell: (rowId: string, colKey: string, value: string) => void;
  onRemoveRow: (rowId: string, anchor?: Element) => void;
  rowAction?: (row: GridRow) => ReactNode;
}) {
  const t = useTranslations("site.ui");

  return (
    <>
      {columns.map((column, colIndex) => {
        const value = row.cells[column.key] ?? "";
        const handleChange = (next: string) =>
          onUpdateCell(row.id, column.key, next);

        return (
          <div
            key={column.key}
            data-grid-first={colIndex === 0 ? "" : undefined}
            className="flex min-w-0 [&>*]:min-w-0"
            style={{ alignItems: "inherit" }}
          >
            {column.renderCell ? (
              column.renderCell({
                row,
                column,
                value,
                disabled,
                onChange: handleChange,
                onChangeCell: (colKey, next) =>
                  onUpdateCell(row.id, colKey, next),
              })
            ) : (
              <Input
                value={value}
                disabled={disabled}
                placeholder={cellPlaceholder?.(colIndex, column)}
                onChange={(event) => handleChange(event.target.value)}
              />
            )}
          </div>
        );
      })}
      {rowAction ? (
        <div
          data-grid-action="extra"
          className="flex items-center justify-center"
        >
          {rowAction(row)}
        </div>
      ) : null}
      {canRemoveRow ? <div data-grid-action="last" className="flex items-center justify-center">
        <AdminTableIconDeleteButton
          size="sm"
          label={t("deleteRow")}
          disabled={!rowDeletable}
          onClick={event => onRemoveRow(row.id, event.currentTarget)}
        />
      </div> : null}
    </>
  );
}

type RowProps = {
  row: GridRow;
  columns: GridColumn[];
  gridTemplate: string;
  gridMinWidth: string;
  alignClass: string;
  disabled?: boolean;
  rowDeletable: boolean;
  canRemoveRow?: boolean;
  cellPlaceholder?: (colIndex: number, column: GridColumn) => string;
  onUpdateCell: (rowId: string, colKey: string, value: string) => void;
  onRemoveRow: (rowId: string, anchor?: Element) => void;
  rowAction?: (row: GridRow) => ReactNode;
  renderRowDetails?: (row: GridRow) => ReactNode;
  rowHeader?: ReactNode;
};

const ROW_SURFACE =
  "bg-[var(--grid-row-bg)] [--grid-row-bg:var(--card)] hover:[--grid-row-bg:color-mix(in_oklab,var(--card)_97%,var(--foreground))]";

function PlainRow({
  gridTemplate,
  gridMinWidth,
  alignClass,
  renderRowDetails,
  rowHeader,
  ...rest
}: RowProps) {
  return (
    <div
      className={cn(
        "grid gap-2 border-b border-border/40 px-3 py-1.5 last:border-b-0",
        settingsCompactControlsClass,
        ROW_SURFACE,
        alignClass,
      )}
      style={{ gridTemplateColumns: gridTemplate, minWidth: gridMinWidth }}
    >
      {rowHeader ? <div data-grid-row-header="" className="col-span-full -mx-3 -mt-1.5">{rowHeader}</div> : null}
      <RowInputs {...rest} />
      {renderRowDetails ? <div data-grid-row-details="" className="col-span-full min-w-0 pb-2">{renderRowDetails(rest.row)}</div> : null}
    </div>
  );
}

function SortableRow({
  gridTemplate,
  gridMinWidth,
  alignClass,
  renderRowDetails,
  rowHeader,
  ...rest
}: RowProps) {
  const t = useTranslations("site.ui");
  const { row, disabled } = rest;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled: disabled || row.locked });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    gridTemplateColumns: gridTemplate,
    minWidth: gridMinWidth,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "grid gap-2 border-b border-border/40 px-3 py-1.5 last:border-b-0",
        settingsCompactControlsClass,
        ROW_SURFACE,
        alignClass,
      )}
    >
      {rowHeader ? <div data-grid-row-header="" className="col-span-full -mx-3 -mt-1.5">{rowHeader}</div> : null}
      <button
        type="button"
        ref={setActivatorNodeRef}
        disabled={disabled || row.locked}
        data-grid-grip=""
        aria-label={t("dragRow")}
        className={cn(
          "flex h-7 cursor-grab touch-none items-center justify-center text-muted-foreground active:cursor-grabbing",
          "disabled:pointer-events-none disabled:opacity-30",
        )}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>
      <RowInputs {...rest} />
      {renderRowDetails ? <div data-grid-row-details="" className="col-span-full min-w-0 pb-2">{renderRowDetails(rest.row)}</div> : null}
    </div>
  );
}

export function useGridFields({
  columns,
  rows,
  onChange,
  disabled = false,
  canAddColumn = true,
  canAddRow = true,
  canRemoveRow = true,
  minColumns = 1,
  maxColumns = 5,
  minRows = 0,
  maxRows = Number.POSITIVE_INFINITY,
  maxVisibleRows = 10,
  maxBodyHeight,
  pagination = false,
  manualPagination = false,
  paginationState,
  onPaginationChange,
  rowCount,
  onAddRow,
  onRequestRemoveRow,
  stickyEdges = false,
  stickyHeader = true,
  sortableColumns = false,
  sortableRows = false,
  hideWhenEmpty = false,
  hideHeader = false,
  repeatRowHeader = false,
  hideActionHeader = false,
  rowAlign = "center",
  bare = false,
  addColumnLabel,
  addRowLabel,
  hideColumnLimitHint = false,
  newColumn,
  newRowCells,
  columnLabelPlaceholder,
  sanitizeColumnLabel,
  cellPlaceholder,
  emptyText,
  rowAction,
  renderRowDetails,
}: GridFieldsEditorProps): { toolbar: ReactNode; table: ReactNode } {
  const t = useTranslations("site.ui");
  const [internalPage, setInternalPage] = useState({ pageIndex: 0, pageSize: 10 });
  const page = paginationState ?? internalPage;
  const totalRows = manualPagination ? rowCount ?? rows.length : rows.length;
  const limitRowCount = pagination && manualPagination ? totalRows : rows.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / page.pageSize));
  const pageIndex = Math.min(page.pageIndex, pageCount - 1);
  const visibleRows = pagination && !manualPagination ? rows.slice(pageIndex * page.pageSize, (pageIndex + 1) * page.pageSize) : rows;
  const changePage = useCallback((next: { pageIndex: number; pageSize: number }) => {
    if (onPaginationChange) onPaginationChange(next);
    else setInternalPage(next);
  }, [onPaginationChange]);
  const pendingAddedRows = useRef<string[]>([]);
  useLayoutEffect(() => {
    if (pendingAddedRows.current.length === 0) return;
    const added = new Set(pendingAddedRows.current);
    pendingAddedRows.current = [];
    if (!pagination || manualPagination) return;
    const lastAddedIndex = rows.findLastIndex(row => added.has(row.id));
    if (lastAddedIndex < 0) return;
    const nextPageIndex = Math.floor(lastAddedIndex / page.pageSize);
    if (nextPageIndex !== page.pageIndex) changePage({ ...page, pageIndex: nextPageIndex });
  }, [rows, pagination, manualPagination, page, changePage]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  const [pendingDeleteColumn, setPendingDeleteColumn] = useState<string | null>(
    null,
  );

  const empty = rows.length === 0;
  const showTable = !(hideWhenEmpty && empty);
  const showAddColumn = canAddColumn && !(hideWhenEmpty && empty);
  useLayoutEffect(() => {
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    if (!body || !header) return;

    const sync = () => {
      header.style.marginRight = `${body.offsetWidth - body.clientWidth}px`;
      header.scrollLeft = body.scrollLeft;
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(body);
    return () => observer.disconnect();
  }, [stickyHeader, hideHeader, showTable, empty]);

  const columnDeletable = (column: GridColumn) =>
    !column.locked && !disabled && columns.length > minColumns;

  function addColumn() {
    if (disabled || columns.length >= maxColumns) return;
    const column = { label: "", ...newColumn, key: newId("col") };
    onChange(prev => prev.columns.length >= maxColumns ? prev : {
      ...prev, columns: [...prev.columns, column],
    });
  }

  function removeColumn(colKey: string) {
    if (disabled) return;
    onChange(prev => {
      const column = prev.columns.find(item => item.key === colKey);
      if (!column || column.locked || prev.columns.length <= minColumns) return prev;
      return {
        columns: prev.columns.filter((item) => item.key !== colKey),
        rows: prev.rows.map((row) => {
          const { [colKey]: _removed, ...rest } = row.cells;
          return { ...row, cells: rest };
        }),
      };
    });
  }

  function updateColumnLabel(colKey: string, label: string) {
    onChange(prev => ({
      ...prev,
      columns: prev.columns.map((item) =>
        item.key === colKey ? { ...item, label } : item,
      ),
    }));
  }

  function addRow() {
    if (disabled || limitRowCount >= maxRows) return;
    if (onAddRow) { onAddRow(); return; }
    const id = newId("row");
    pendingAddedRows.current.push(id);
    onChange(prev => {
      const count = pagination && manualPagination ? rowCount ?? prev.rows.length : prev.rows.length;
      if (count >= maxRows) return prev;
      const cells = newRowCells ? newRowCells(prev.rows) : {};
      return { ...prev, rows: [...prev.rows, { id, cells }] };
    });
  }

  function removeRow(rowId: string, anchor?: Element) {
    if (disabled || !canRemoveRow) return;
    const remove = () => onChange(prev => {
      const row = prev.rows.find(item => item.id === rowId);
      const count = pagination && manualPagination ? rowCount ?? prev.rows.length : prev.rows.length;
      if (!row || row.locked || count <= minRows) return prev;
      return { ...prev, rows: prev.rows.filter(item => item.id !== rowId) };
    });
    const row = rows.find(item => item.id === rowId);
    if (!row || row.locked || limitRowCount <= minRows) return;
    if (onRequestRemoveRow) onRequestRemoveRow(row, remove, anchor);
    else remove();
  }

  function updateCell(rowId: string, colKey: string, value: string) {
    onChange((prev) => ({
      columns: prev.columns,
      rows: prev.rows.map((row) =>
        row.id === rowId
          ? { ...row, cells: { ...row.cells, [colKey]: value } }
          : row,
      ),
    }));
  }

  function handleColumnDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onChange(prev => {
      const from = prev.columns.findIndex((item) => item.key === active.id);
      const to = prev.columns.findIndex((item) => item.key === over.id);
      if (from < 0 || to < 0) return prev;
      const next = arrayMove(prev.columns, from, to);

      const lockedStable = prev.columns.every(
        (item, index) => !item.locked || next[index]?.key === item.key,
      );
      return lockedStable ? { ...prev, columns: next } : prev;
    });
  }

  function handleRowDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onChange(prev => {
      const from = prev.rows.findIndex((item) => item.id === active.id);
      const to = prev.rows.findIndex((item) => item.id === over.id);
      if (from < 0 || to < 0) return prev;
      const next = arrayMove(prev.rows, from, to);

      const lockedStable = prev.rows.every(
        (item, index) => !item.locked || next[index]?.id === item.id,
      );
      return lockedStable ? { ...prev, rows: next } : prev;
    });
  }

  const actionTracks = [...(rowAction ? [ROW_ACTION_TRACK_WIDTH] : []), ...(canRemoveRow ? [ROW_ACTION_TRACK_WIDTH] : [])];
  const trackWidths = [
    ...(sortableRows ? [GRIP_TRACK_WIDTH] : []),
    ...columns.map((column) => column.width ?? FLEX_COLUMN_MIN_WIDTH),
    ...actionTracks,
  ];
  const gridTemplate = [
    ...(sortableRows ? [GRIP_TRACK_WIDTH] : []),
    ...columns.map(
      (column) => column.width ?? `minmax(${FLEX_COLUMN_MIN_WIDTH}, 1fr)`,
    ),
    ...actionTracks,
  ].join(" ");

  const gridMinWidth = `calc(${trackWidths.join(" + ")} + ${
    (trackWidths.length - 1) * GRID_GAP_REM + ROW_PADDING_X_REM * 2
  }rem)`;
  const rowAlignClass = rowAlign === "start" ? "items-start" : "items-center";
  const toolbarBtnClass = cn(
    "inline-flex h-7 items-center gap-1 text-xs text-primary transition-colors hover:text-primary/80",
    "disabled:pointer-events-none disabled:opacity-50",
  );

  const columnHeaderProps = (column: GridColumn): ColumnHeaderProps => ({
    column,
    first: column === columns[0],
    deletable: columnDeletable(column),
    disabled,
    placeholder: columnLabelPlaceholder,
    sanitize: sanitizeColumnLabel,
    onCommit: updateColumnLabel,
    onRemove: setPendingDeleteColumn,
  });

  const pendingColumn = pendingDeleteColumn
    ? columns.find((item) => item.key === pendingDeleteColumn)
    : null;

  const deleteColumnDialog = (
    <AdminDeleteConfirmDialog
      open={pendingDeleteColumn !== null}
      onOpenChange={(next) => {
        if (!next) setPendingDeleteColumn(null);
      }}
      title={t("deleteColumnTitle")}
      content={t("deleteColumnContent", {
        name: pendingColumn?.label?.trim() || t("thisColumn"),
      })}
      onCancel={() => setPendingDeleteColumn(null)}
      onConfirm={() => {
        if (pendingDeleteColumn) removeColumn(pendingDeleteColumn);
        setPendingDeleteColumn(null);
      }}
    />
  );

  const toolbar = (
    <div className="flex flex-wrap items-center gap-3">
      {canAddRow ? (
        <button
          type="button"
          disabled={disabled || limitRowCount >= maxRows}
          className={toolbarBtnClass}
          onClick={addRow}
        >
          <Plus className="size-4" />
          {addRowLabel ?? t("addRow")}
        </button>
      ) : null}
      {showAddColumn ? (
        <>
          <button
            type="button"
            disabled={disabled || columns.length >= maxColumns}
            className={toolbarBtnClass}
            onClick={addColumn}
          >
            <Plus className="size-4" />
            {addColumnLabel ?? t("addColumn")}
          </button>
          {!hideColumnLimitHint && Number.isFinite(maxColumns) ? (
            <span className="text-xs text-muted-foreground">
              {t("maxColumns", { count: maxColumns })}
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );

  const header = hideHeader ? null : (
    <div
      className="grid gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
      style={
        {
          gridTemplateColumns: gridTemplate,
          minWidth: gridMinWidth,
          "--grid-row-bg": "color-mix(in oklab, var(--muted) 40%, var(--card))",
        } as CSSProperties
      }
    >
      {sortableRows ? <span data-grid-grip="" aria-hidden /> : null}
      {sortableColumns ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleColumnDragEnd}
        >
          <SortableContext
            items={columns.map((column) => column.key)}
            strategy={horizontalListSortingStrategy}
          >
            {columns.map((column) => (
              <SortableColumnHeader
                key={column.key}
                {...columnHeaderProps(column)}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        columns.map((column) => (
          <PlainColumnHeader
            key={column.key}
            {...columnHeaderProps(column)}
          />
        ))
      )}
      {rowAction ? <span data-grid-action="extra" aria-hidden /> : null}
      {canRemoveRow ? <span
        data-grid-action="last"
        className="flex items-center justify-center"
      >
        <span className={hideActionHeader ? "sr-only" : undefined}>{t("actions")}</span>
      </span> : null}
    </div>
  );

  const tableBody = showTable ? (
    <div
      data-grid-editor=""
      className={cn(
        "bg-card",
        !bare && "overflow-hidden rounded-md border border-border/70",
        stickyEdges &&
          "[&_[data-grid-first]]:sticky [&_[data-grid-first]]:left-[var(--grid-first-left)] [&_[data-grid-first]]:z-10 [&_[data-grid-first]]:self-stretch [&_[data-grid-first]]:bg-[var(--grid-row-bg,var(--card))] [&_[data-grid-first]]:shadow-[0.5rem_0_var(--grid-row-bg,var(--card)),-0.5rem_0_var(--grid-row-bg,var(--card))] [&_[data-grid-grip]]:sticky [&_[data-grid-grip]]:left-3 [&_[data-grid-grip]]:z-10 [&_[data-grid-grip]]:bg-[var(--grid-row-bg,var(--card))] [&_[data-grid-grip]]:shadow-[0.75rem_0_var(--grid-row-bg,var(--card)),-0.75rem_0_var(--grid-row-bg,var(--card))] [&_[data-grid-action]]:sticky [&_[data-grid-action]]:z-10 [&_[data-grid-action]]:self-stretch [&_[data-grid-action]]:bg-[var(--grid-row-bg,var(--card))] [&_[data-grid-action]]:shadow-[0.75rem_0_var(--grid-row-bg,var(--card)),-0.75rem_0_var(--grid-row-bg,var(--card))] [&_[data-grid-action=last]]:right-3 [&_[data-grid-action=extra]]:right-[var(--grid-extra-right)]",
      )}
      style={
        { "--grid-first-left": sortableRows ? "2.5rem" : "0.75rem", "--grid-extra-right": canRemoveRow ? "3.25rem" : "0.75rem" } as CSSProperties
      }
    >
      {stickyHeader && !hideHeader && (!repeatRowHeader || empty) ? (
        <div
          ref={headerScrollRef}
          data-grid-header-scroll=""
          className="overflow-hidden"
        >
          {header}
        </div>
      ) : null}
      <div
        ref={bodyScrollRef}
        data-grid-scroll=""
        className={cn(
          "overflow-auto",
          maxVisibleRows > 0 && "overscroll-contain",

          stickyEdges &&
            "[&_[data-grid-first]]:after:pointer-events-none [&_[data-grid-first]]:after:absolute [&_[data-grid-first]]:after:-top-1.5 [&_[data-grid-first]]:after:-bottom-[7px] [&_[data-grid-first]]:after:-right-1 [&_[data-grid-first]]:after:w-px [&_[data-grid-first]]:after:bg-border/70 [&_[data-grid-first]]:after:shadow-[3px_0_4px_#00000012] [&_[data-grid-first]]:after:content-['']",
          stickyEdges &&
            (rowAction
              ? "[&_[data-grid-action=extra]]:before:pointer-events-none [&_[data-grid-action=extra]]:before:absolute [&_[data-grid-action=extra]]:before:-top-1.5 [&_[data-grid-action=extra]]:before:-bottom-[7px] [&_[data-grid-action=extra]]:before:-left-1.5 [&_[data-grid-action=extra]]:before:w-px [&_[data-grid-action=extra]]:before:bg-border/70 [&_[data-grid-action=extra]]:before:shadow-[-3px_0_4px_#00000012] [&_[data-grid-action=extra]]:before:content-['']"
              : "[&_[data-grid-action=last]]:before:pointer-events-none [&_[data-grid-action=last]]:before:absolute [&_[data-grid-action=last]]:before:-top-1.5 [&_[data-grid-action=last]]:before:-bottom-[7px] [&_[data-grid-action=last]]:before:-left-1.5 [&_[data-grid-action=last]]:before:w-px [&_[data-grid-action=last]]:before:bg-border/70 [&_[data-grid-action=last]]:before:shadow-[-3px_0_4px_#00000012] [&_[data-grid-action=last]]:before:content-['']"),
        )}
        style={{
          maxHeight: maxVisibleRows > 0
            ? maxBodyHeight != null && maxBodyHeight !== "none"
              ? `min(${(maxVisibleRows + (!stickyHeader && !hideHeader ? 1 : 0)) * 2.5625}rem, ${typeof maxBodyHeight === "number" ? `${maxBodyHeight}px` : maxBodyHeight})`
              : `${(maxVisibleRows + (!stickyHeader && !hideHeader ? 1 : 0)) * 2.5625}rem`
            : maxBodyHeight,
          scrollbarGutter: stickyHeader && !hideHeader ? "stable" : undefined,
        }}
        onScroll={(event) => {
          if (headerScrollRef.current) {
            headerScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }
        }}
      >
        {stickyHeader || (repeatRowHeader && !empty) ? null : header}

        {empty ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            {emptyText ?? t("noData")}
          </div>
        ) : sortableRows ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleRowDragEnd}
          >
            <SortableContext
              items={visibleRows.map((row) => row.id)}
              strategy={verticalListSortingStrategy}
            >
              {visibleRows.map((row) => (
                <SortableRow
                  key={row.id}
                  row={row}
                  columns={columns}
                  gridTemplate={gridTemplate}
                  gridMinWidth={gridMinWidth}
                  alignClass={rowAlignClass}
                  disabled={disabled}
                  rowDeletable={!row.locked && !disabled && limitRowCount > minRows}
                  canRemoveRow={canRemoveRow}
                  cellPlaceholder={cellPlaceholder}
                  onUpdateCell={updateCell}
                  onRemoveRow={removeRow}
                  rowAction={rowAction}
                  renderRowDetails={renderRowDetails}
                  rowHeader={repeatRowHeader ? header : null}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          visibleRows.map((row) => (
            <PlainRow
              key={row.id}
              row={row}
              columns={columns}
              gridTemplate={gridTemplate}
              gridMinWidth={gridMinWidth}
              alignClass={rowAlignClass}
              disabled={disabled}
              rowDeletable={!row.locked && !disabled && limitRowCount > minRows}
              canRemoveRow={canRemoveRow}
              cellPlaceholder={cellPlaceholder}
              onUpdateCell={updateCell}
              onRemoveRow={removeRow}
              rowAction={rowAction}
              renderRowDetails={renderRowDetails}
              rowHeader={repeatRowHeader ? header : null}
            />
          ))
        )}
      </div>
    </div>
  ) : null;

  const table = (
    <>
      {tableBody}
      {pagination && totalRows > 0 ? <div className="border-t border-border/60 px-3 py-2"><DataTablePagination pageIndex={pageIndex} pageCount={pageCount} pageSize={page.pageSize} filteredTotal={totalRows} pageSizeOptions={[10, 20, 50, 100]} showPageSizeSelector showPageJumpInput disabled={disabled} onPageIndexChange={next => changePage({ ...page, pageIndex: next })} onPageSizeChange={pageSize => changePage({ pageIndex: 0, pageSize })} /></div> : null}
      {deleteColumnDialog}
    </>
  );

  return { toolbar, table };
}

export function GridFieldsEditor(props: GridFieldsEditorProps) {
  const { toolbar, table } = useGridFields(props);

  return (
    <div className="w-full space-y-2">
      {toolbar}
      {table}
    </div>
  );
}

export function useGridDataState({
  value,
  parse,
  serialize,
  onChange,
}: {
  value: string;
  parse: (value: string) => GridData;
  serialize: (data: GridData) => string;
  onChange: (value: string) => void;
}): [GridData, (next: GridDataUpdater) => void] {
  const parseRef = useRef(parse);
  parseRef.current = parse;
  const serializeRef = useRef(serialize);
  serializeRef.current = serialize;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [data, setData] = useState<GridData>(() => parse(value));
  const dataRef = useRef(data);
  dataRef.current = data;
  const lastSerialized = useRef(value);

  useEffect(() => {

    if (value !== lastSerialized.current) {
      lastSerialized.current = value;
      const parsed = parseRef.current(value);
      dataRef.current = parsed;
      setData(parsed);
    }
  }, [value]);

  const update = useCallback((next: GridDataUpdater) => {
    const resolved = resolveGridData(next, dataRef.current);
    dataRef.current = resolved;
    const serialized = serializeRef.current(resolved);
    lastSerialized.current = serialized;
    setData(resolved);
    onChangeRef.current(serialized);
  }, []);

  return [data, update];
}
